import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faWrench } from "@fortawesome/free-solid-svg-icons";
import { useDevTools } from "./DevToolsProvider";
import type { DevToolsPlugin, PluginStatus } from "./types";

function statusClass(state?: PluginStatus["state"]): string {
  if (!state) return "";
  return `dt-icon-btn--status-${state}`;
}

function badgeClass(state: PluginStatus["state"]): string {
  if (state === "warning") return "dt-badge dt-badge--warning";
  if (state === "error") return "dt-badge dt-badge--error";
  return "dt-badge";
}

const STATUS_PRIORITY: Record<string, number> = {
  error: 4,
  warning: 3,
  active: 2,
  success: 1,
  loading: 0,
  inactive: -1,
};

// Each plugin icon is its own component so useStatus (which may contain hooks)
// is called at the top level of a component, not inside a loop.
function PluginIconButton({
  plugin,
  groupName,
  isSelected,
  isDrawerOpen,
  onSelect,
  onStatusReport,
}: {
  plugin: DevToolsPlugin;
  groupName?: string;
  isSelected: boolean;
  isDrawerOpen: boolean;
  onSelect: () => void;
  onStatusReport: (status: PluginStatus | undefined) => void;
}) {
  const [hovered, setHovered] = useState(false);
  const status: PluginStatus | undefined = plugin.useStatus?.();
  // Called during render — the parent must collect into a ref, not setState,
  // or React will throw "Cannot update a component while rendering a different component".
  onStatusReport(status);

  const showSelected = isSelected && isDrawerOpen;
  const shouldPulse =
    !showSelected &&
    (status?.state === "active" || status?.state === "warning") &&
    status?.badge != null;
  const btnClass = [
    "dt-icon-btn",
    showSelected ? "dt-icon-btn--active" : statusClass(status?.state),
    shouldPulse ? "dt-icon-btn--pulsing" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const label = groupName ? `${groupName} • ${plugin.name}` : plugin.name;

  return (
    <button
      className={btnClass}
      onClick={onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={label}
      type="button"
    >
      <FontAwesomeIcon icon={plugin.icon} />
      {status && status.state !== "inactive" && status.badge != null && (
        <span className={badgeClass(status.state)}>{status.badge}</span>
      )}
      {hovered && (
        <span className="dt-tooltip">
          {label}
          {status?.summary ? ` - ${status.summary}` : ""}
        </span>
      )}
    </button>
  );
}

export function DevToolsSidebar() {
  const {
    entries,
    activePluginId,
    isDrawerOpen,
    isSidebarExpanded,
    setActivePlugin,
    toggleSidebar,
  } = useDevTools();

  // Collect statuses reported by each PluginIconButton for FAB aggregation.
  // This is populated synchronously during render via onStatusReport callbacks.
  const statusMap = useRef<Map<string, PluginStatus | undefined>>(new Map());

  // dt-no-anim suppresses the CSS transition while the className-driven
  // re-render (setHasMounted below) flips the className from
  // "dt-sidebar dt-no-anim" to "dt-sidebar". With lazy useState in
  // DevToolsProvider, the persisted state is in the very first render,
  // so the element is *created* with the correct collapsed/expanded class
  // — element creation alone doesn't trigger CSS transitions. dt-no-anim
  // covers the one re-render that removes itself. rAF defers that re-render
  // to a frame after the first paint so the browser has cached a "previous"
  // computed style with transition: none.
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setHasMounted(true));
    return () => cancelAnimationFrame(raf);
  }, []);
  const noAnim = hasMounted ? "" : "dt-no-anim";

  // Compute aggregated status from collected statuses
  let worstState: PluginStatus["state"] = "inactive";
  let worstPriority = -1;
  let issueCount = 0;
  for (const status of statusMap.current.values()) {
    if (!status) continue;
    const priority = STATUS_PRIORITY[status.state] ?? -1;
    if (priority > worstPriority) {
      worstPriority = priority;
      worstState = status.state;
    }
    if (status.state === "error" || status.state === "warning") {
      issueCount++;
    }
  }

  return (
    <>
      {/* FAB — always rendered, hidden when sidebar expanded */}
      <button
        className={[
          "dt-fab",
          isSidebarExpanded ? "dt-fab--hidden" : "",
          worstState === "error" || worstState === "warning"
            ? statusClass(worstState)
            : "",
          worstState === "error" || worstState === "warning"
            ? "dt-icon-btn--pulsing"
            : "",
          noAnim,
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={toggleSidebar}
        title="Open DevTools"
        type="button"
        suppressHydrationWarning
      >
        <FontAwesomeIcon icon={faWrench} />
        {issueCount > 0 && (
          <span className={badgeClass(worstState)}>{issueCount}</span>
        )}
      </button>

      {/* Sidebar — always rendered, slides off-screen when collapsed */}
      <div
        className={[
          "dt-sidebar",
          isSidebarExpanded ? "" : "dt-sidebar--collapsed",
          noAnim,
        ]
          .filter(Boolean)
          .join(" ")}
        suppressHydrationWarning
      >
        {entries.map((entry, i) =>
          entry.type === "separator" ? (
            <div key={`sep-${i}`} className="dt-separator" role="separator" />
          ) : (
            <PluginIconButton
              key={entry.plugin.id}
              plugin={entry.plugin}
              groupName={entry.groupName}
              isSelected={activePluginId === entry.plugin.id}
              isDrawerOpen={isDrawerOpen}
              onSelect={() => setActivePlugin(entry.plugin.id)}
              onStatusReport={(status) =>
                statusMap.current.set(entry.plugin.id, status)
              }
            />
          ),
        )}
        <div className="dt-sidebar__spacer" />
        <button
          className="dt-icon-btn"
          onClick={toggleSidebar}
          title="Collapse"
          type="button"
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
      </div>
    </>
  );
}
